/**
 * VideoSourcePicker — three-tab admin control for supplying a video:
 *
 *   1. URL         — YouTube share/embed, Vimeo player URL, direct MP4/WEBM/MOV
 *   2. Upload      — direct file upload (chunked, backed by Object Storage);
 *                    delegated to the caller via an `uploadSlot` render prop
 *                    so this component doesn't need to know about intros vs
 *                    module videos (different endpoints + progress UIs).
 *   3. Vimeo Embed — paste the raw `<iframe …>` HTML the Vimeo Share dialog
 *                    provides. On paste we extract the `src` attribute and
 *                    store THAT as the video URL so the underlying schema
 *                    stays a single URL field (backwards compatible).
 *
 * All three tabs read + write the SAME `value` / `onChange` prop. Only the
 * UI differs. This keeps the persisted shape backwards-compatible and lets
 * the user-facing renderers (IntroSection, ModuleDetail) stay untouched —
 * `normalizeVideoUrl` already routes Vimeo player URLs to an `<iframe>`.
 */
import React, { useMemo, useState } from "react";
import { Link as LinkIcon, Upload as UploadIcon, Code2, AlertTriangle } from "lucide-react";

const TABS = [
  { id: "url",      label: "Video URL",         icon: LinkIcon },
  { id: "upload",   label: "Video Upload",      icon: UploadIcon },
  { id: "vimeo",    label: "Vimeo Embed Code",  icon: Code2 },
];

/**
 * Pick the initial tab based on the current stored value:
 *   • Empty                            → URL
 *   • Starts with `<` (raw embed HTML) → Vimeo Embed Code
 *   • Points at our /api/…/asset/ path → Upload
 *   • Anything else (external URL)     → URL
 * The admin can flip tabs at any time; the underlying value stays the same.
 */
function guessInitialTab(value, uploadPathPrefix) {
  if (!value) return "url";
  const v = String(value).trim();
  if (v.startsWith("<")) return "vimeo";
  if (uploadPathPrefix && v.startsWith(uploadPathPrefix)) return "upload";
  return "url";
}

export default function VideoSourcePicker({
  value = "",
  onChange,
  uploadSlot = null,
  uploadPathPrefix = "",
  testIdPrefix = "video-source",
  urlPlaceholder = "https://youtu.be/... or https://vimeo.com/...",
}) {
  const [tab, setTab] = useState(() => guessInitialTab(value, uploadPathPrefix));
  // Re-derive the tab whenever the stored `value` changes shape
  // (empty → URL → embed → upload). Before this fix the initial
  // guess only ran on mount, so switching intros in the admin
  // console left the Vimeo tab open while the newly-loaded intro
  // had a plain URL (or vice-versa) — admins had to manually
  // click the right tab every time.
  const guessed = useMemo(() => guessInitialTab(value, uploadPathPrefix), [value, uploadPathPrefix]);
  React.useEffect(() => {
    setTab(guessed);
  }, [guessed]);

  // Vimeo tab writes the RAW HTML verbatim through to the stored
  // value — no src extraction, no reformatting. That's the whole
  // point of a "Vimeo Embed Code" option: the outer `<div>` carries
  // the aspect ratio + the `<script>` powers chapters / analytics.
  const isCurrentEmbed = value && String(value).trim().startsWith("<");
  const embedInvalid = useMemo(() => {
    if (tab !== "vimeo" || !value) return false;
    return !isCurrentEmbed;
  }, [tab, value, isCurrentEmbed]);

  return (
    <div>
      <div
        role="tablist"
        aria-label="Video source"
        data-testid={`${testIdPrefix}-tabs`}
        className="inline-flex items-center gap-1 rounded-full border border-shell-border bg-white p-1 text-[11px] font-semibold"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.id)}
              data-testid={`${testIdPrefix}-tab-${t.id}`}
              className={[
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition",
                active
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              <Icon className="w-3 h-3" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-3">
        {tab === "url" && (
          <input
            type="url"
            value={isCurrentEmbed ? "" : value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={urlPlaceholder}
            data-testid={`${testIdPrefix}-url-input`}
            className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
          />
        )}

        {tab === "upload" && (
          <div data-testid={`${testIdPrefix}-upload-slot`}>
            {uploadSlot || (
              <p className="text-xs text-muted-foreground">
                Upload is not available for this content type.
              </p>
            )}
          </div>
        )}

        {tab === "vimeo" && (
          <div>
            <textarea
              value={isCurrentEmbed ? value : ""}
              onChange={(e) => onChange(e.target.value)}
              rows={6}
              placeholder={`<div style="padding:56.25% 0 0 0;position:relative;"><iframe src="https://player.vimeo.com/video/…" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe></div>`}
              data-testid={`${testIdPrefix}-vimeo-textarea`}
              className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary/40"
            />
            {isCurrentEmbed && (
              <div
                className="mt-2 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
                data-testid={`${testIdPrefix}-vimeo-saved`}
              >
                <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white text-[10px] font-bold">✓</span>
                <span className="leading-snug">
                  Embed code saved verbatim — {String(value).length.toLocaleString()} characters
                  will be rendered exactly as pasted on the member page.
                </span>
              </div>
            )}
            {embedInvalid && (
              <div
                className="mt-2 flex items-start gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-xs text-foreground"
                data-testid={`${testIdPrefix}-vimeo-error`}
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span className="leading-snug">
                  This tab expects a raw <code className="rounded bg-white/70 px-1 py-0.5 text-[11px]">&lt;iframe&gt;</code>{" "}
                  or <code className="rounded bg-white/70 px-1 py-0.5 text-[11px]">&lt;div&gt;&lt;iframe&gt;…&lt;/div&gt;</code>{" "}
                  paste — the current value looks like a plain URL. Switch to the{" "}
                  <b>Video URL</b> tab, or paste the complete embed HTML here.
                </span>
              </div>
            )}
            <p className="mt-2 text-[11px] text-muted-foreground">
              Paste the <b>complete</b> HTML from Vimeo → Share → Embed (including the outer{" "}
              <code>&lt;div&gt;</code> and any <code>&lt;script&gt;</code>). It's saved verbatim
              and rendered exactly as pasted on the member page.
            </p>
          </div>
        )}
      </div>

      {value && !isCurrentEmbed && (
        <p
          className="mt-2 text-[11px] text-muted-foreground truncate"
          data-testid={`${testIdPrefix}-current`}
        >
          Current source: <span className="font-mono break-all">{value}</span>
        </p>
      )}
    </div>
  );
}
